/**
 * Logística → Gastos (`/logistica/gastos`).
 *
 * Puerto de `Logistics::expenses` + `views/expenses/index.php` (que se alimentaba
 * de `API/Gastos`) y de sus acciones `Gastos::update_expense` y `Gastos::delete_gasto`:
 * la tabla de gastos del turno con sus filtros (fechas, tipo y monto mínimo), la
 * edición de la descripción y el monto, y la inhabilitación del gasto.
 *
 * Las acciones se ofrecen al dueño del gasto o a un administrador, sólo en turnos
 * verificados y con su permiso (`liquidaciones.gastos.editar` / `.eliminar`), como
 * el panel anterior; el resto de filas muestran el candado con el motivo. El **vale
 * del gasto** ya lo genera el propio panel (PDF de la API); el envío a la impresora
 * térmica se portará con el circuito de impresión.
 */

import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Filter,
  Lock,
  Pencil,
  Printer,
  Receipt,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { useEditarGasto, useEliminarGasto, useGastos } from '../gastos.api';
import type { Gasto, GastoEditable, GastosFiltros } from '../gastos.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Tipos de gasto del panel anterior. */
const TIPOS = [
  { valor: '1', etiqueta: 'Diario' },
  { valor: '2', etiqueta: 'Fijo' },
];

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Diálogo para editar la descripción y el monto de un gasto. */
function EditarGastoDialogo({
  gasto,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  gasto: Gasto;
  guardando: boolean;
  error: string | null;
  onGuardar: (datos: GastoEditable) => void;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState<GastoEditable>({
    descripcion: gasto.descripcion ?? '',
    cantidad: String(gasto.cantidad),
  });

  const cantidadInvalida = datos.cantidad === '' || Number(datos.cantidad) < 0;
  const puedeGuardar =
    datos.descripcion.trim() !== '' && !cantidadInvalida && !guardando;

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="md"
      title="Editar gasto"
      description={`Registro #${gasto.id} · ${gasto.usuario || 'usuario no informado'}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cerrar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() => onGuardar(datos)}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="info">
          Al cambiar el monto se ajusta el efectivo esperado del turno: se devuelve el gasto
          anterior y se aplica el nuevo.
        </Alert>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="gasto-descripcion" className="text-sm font-medium text-ink-700">
            Descripción
          </label>
          <textarea
            id="gasto-descripcion"
            rows={4}
            maxLength={300}
            className={cn(CONTROL, 'h-auto py-2')}
            value={datos.descripcion}
            onChange={(evento) => setDatos({ ...datos, descripcion: evento.target.value })}
          />
          <p className="text-xs text-ink-400">{datos.descripcion.length} de 300 caracteres</p>
        </div>

        <Input
          label="Monto"
          type="number"
          step="0.01"
          min={0}
          value={datos.cantidad}
          error={cantidadInvalida ? 'El monto no puede estar vacío ni ser negativo.' : undefined}
          onChange={(evento) => setDatos({ ...datos, cantidad: evento.target.value })}
        />
      </div>
    </Dialog>
  );
}

export function GastosPage() {
  useDocumentTitle('Logística · Gastos');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [tipo, setTipo] = useState('');
  const [montoMin, setMontoMin] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<GastosFiltros>({
    orden: 'created_at',
    direccion: 'DESC',
    perPage: POR_PAGINA,
    page: 1,
  });
  const [editando, setEditando] = useState<Gasto | null>(null);
  const [aEliminar, setAEliminar] = useState<Gasto | null>(null);
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const gastos = useGastos(filtros);
  const guardar = useEditarGasto();
  const eliminar = useEliminarGasto();

  const sinPermiso = gastos.error instanceof ApiError && gastos.error.status === 403;
  const lista = gastos.data?.gastos ?? [];
  const total = gastos.data?.total ?? 0;
  const pagina = gastos.data?.page ?? 1;
  const paginas = gastos.data?.paginas ?? 1;
  const permisos = gastos.data?.permisos;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica los filtros y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      tipo: tipo || undefined,
      montoMin: montoMin.trim() || undefined,
      search: busqueda.trim() || undefined,
      orden: 'created_at',
      direccion: 'DESC',
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  /** Motivo por el que una fila no se puede tocar. */
  function motivoBloqueo(gasto: Gasto, accion: string): string {
    if (!gasto.activo) return `${accion} · el gasto ya está inhabilitado`;
    if (!permisos?.[accion === 'Editar' ? 'editar' : 'eliminar']) {
      return `${accion} · falta el permiso liquidaciones.gastos.${accion === 'Editar' ? 'editar' : 'eliminar'}`;
    }
    if (Number(gasto.estado_verificacion) !== 1) {
      return `${accion} · sólo en turnos verificados`;
    }

    return `${accion} · sólo el usuario que registró el gasto (o un administrador)`;
  }

  /** Abre el vale del gasto (lo genera la propia API). */
  async function imprimirVale(gasto: Gasto) {
    setErrorImpresion(null);
    setImprimiendo(gasto.id);

    try {
      await abrirPdfAutenticado(`/api/logistica/gastos/${gasto.id}/voucher`);
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar el vale.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Receipt className="size-6 text-brand-500" aria-hidden="true" />
          Gastos
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Gastos registrados en los turnos, con su edición y su inhabilitación.
        </p>
      </div>

      {gastos.data ? <Badge variant="outline">{formatNumber(total)} registros</Badge> : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            {mensajeDe(gastos.error, 'Esta pantalla es del grupo logistics.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        La <strong>edición</strong> y la <strong>inhabilitación</strong> ya están disponibles para el
        dueño del gasto (o un administrador) en turnos verificados y con su permiso. El{' '}
        <strong>vale del gasto</strong> ya lo genera el propio panel; el envío a la impresora
        térmica se portará con el circuito de impresión.
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="ga-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="ga-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ga-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="ga-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ga-tipo" className="text-sm font-medium text-ink-700">
                Tipo de gasto
              </label>
              <Select
                id="ga-tipo"
                className={cn(CONTROL, 'w-40')}
                value={tipo}
                onChange={(evento) => setTipo(evento.target.value)}
              >
                <option value="">Todos</option>
                {TIPOS.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ga-monto" className="text-sm font-medium text-ink-700">
                Monto mínimo
              </label>
              <input
                id="ga-monto"
                type="number"
                step="0.01"
                min={0}
                className={cn(CONTROL, 'w-32')}
                placeholder="0.00"
                value={montoMin}
                onChange={(evento) => setMontoMin(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ga-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="ga-buscar"
                className={cn(CONTROL, 'w-48')}
                placeholder="Descripción, usuario…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                setDesde('');
                setHasta('');
                setTipo('');
                setMontoMin('');
                setBusqueda('');
                setFiltros({
                  orden: 'created_at',
                  direccion: 'DESC',
                  perPage: POR_PAGINA,
                  page: 1,
                });
              }}
            >
              Limpiar
            </Button>
          </div>

          {guardar.error || eliminar.error ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {mensajeDe(guardar.error ?? eliminar.error, 'La API no respondió.')}
            </Alert>
          ) : null}

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar el vale">
              {errorImpresion}
            </Alert>
          ) : null}

          {gastos.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : gastos.error ? (
            <Alert variant="error" title="No se pudieron leer los gastos">
              {mensajeDe(gastos.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay gastos con esos filtros.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">#</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Personal</th>
                    <th className="py-2">Gasto</th>
                    <th className="py-2 text-right">Cantidad</th>
                    <th className="py-2">Tipo</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((gasto) => (
                    <tr key={gasto.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3 text-ink-500">{gasto.id}</td>
                      <td className="py-3 text-ink-700">
                        <span className="block">{(gasto.creado ?? '').slice(0, 10)}</span>
                        <span className="block text-[11px] text-ink-400">
                          {(gasto.creado ?? '').slice(11)}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">
                          {gasto.usuario || '—'}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {gasto.turno_id ? `Turno #${gasto.turno_id}` : 'Sin turno'}
                        </span>
                      </td>
                      <td className="max-w-[280px] py-3 text-ink-600">
                        {gasto.descripcion || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3 text-right font-semibold text-ink-900">
                        {formatCurrency(gasto.cantidad)}
                      </td>
                      <td className="py-3">
                        <Badge variant={gasto.tipo === 1 ? 'brand' : 'neutral'}>
                          {gasto.tipo_legible}
                        </Badge>
                        {!gasto.activo ? (
                          <span className="mt-1 block">
                            <Badge variant="danger">Inhabilitado</Badge>
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Imprimir el vale del gasto"
                            aria-label={`Imprimir el vale del gasto ${gasto.id}`}
                            loading={imprimiendo === gasto.id}
                            onClick={() => void imprimirVale(gasto)}
                          >
                            <Printer className="size-4 text-ink-600" aria-hidden="true" />
                          </Button>

                          {gasto.puede_editar ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar gasto"
                              aria-label={`Editar el gasto ${gasto.id}`}
                              onClick={() => {
                                guardar.reset();
                                setEditando(gasto);
                              }}
                            >
                              <Pencil className="size-4 text-amber-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title={motivoBloqueo(gasto, 'Editar')}
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}

                          {gasto.puede_eliminar ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Eliminar gasto"
                              aria-label={`Eliminar el gasto ${gasto.id}`}
                              onClick={() => {
                                eliminar.reset();
                                setAEliminar(gasto);
                              }}
                            >
                              <Ban className="size-4 text-red-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title={motivoBloqueo(gasto, 'Eliminar')}
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} registros · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {editando ? (
        <EditarGastoDialogo
          gasto={editando}
          guardando={guardar.isPending}
          error={guardar.error ? mensajeDe(guardar.error, 'No se pudo guardar el gasto.') : null}
          onGuardar={(datos) =>
            guardar.mutate({ id: editando.id, gasto: datos }, { onSuccess: () => setEditando(null) })
          }
          onCerrar={() => {
            guardar.reset();
            setEditando(null);
          }}
        />
      ) : null}

      {aEliminar ? (
        <ConfirmDialog
          open
          onClose={() => setAEliminar(null)}
          onConfirm={() => eliminar.mutate(aEliminar.id, { onSuccess: () => setAEliminar(null) })}
          title="¿Eliminar el gasto?"
          description="El gasto quedará inhabilitado y su monto volverá al efectivo esperado del turno en el que se registró, como en el panel anterior."
          confirmLabel="Sí, eliminar"
          loading={eliminar.isPending}
        />
      ) : null}
    </div>
  );
}
